import type {
  AuthorizeApplePayResult,
  AuthorizePaymentInput,
  ApplePayPaymentRequests,
  ValidateApplePayMerchant,
} from '@mangopay/checkout-sdk-core';
import { applePayDefaultOption } from './apple-pay-default-option';

interface ApplePayEvent {
  sessionError?: unknown;
  [key: string]: unknown;
}

interface ApplePayValidateMerchantEvent extends ApplePayEvent {
  validationURL: string;
}

interface ApplePayPaymentAuthorizedEvent extends ApplePayEvent {
  payment?: {
    token?: unknown;
  };
}

interface ApplePaySessionLike {
  onvalidatemerchant: (event: ApplePayValidateMerchantEvent) => void;
  onpaymentauthorized: (event: ApplePayPaymentAuthorizedEvent) => void;
  oncancel: () => void;
  begin(): void;
  abort(): void;
  completeMerchantValidation(merchantSession: unknown): void;
  completePayment(status: number): void;
}

interface ApplePaySessionConstructor {
  new (version: number, paymentRequest: ApplePayPaymentRequests): ApplePaySessionLike;
  canMakePayments(): boolean;
  STATUS_FAILURE: number;
  STATUS_SUCCESS: number;
}

// Global Apple Pay constructor provided by the browser (only available in
// supporting environments). We type just the subset we use at runtime.
declare const ApplePaySession: ApplePaySessionConstructor | undefined;

export class ApplePayPaymentRequest {
  private readonly authorizeRequestInput: AuthorizePaymentInput;

  private readonly applePayRequest: ApplePayPaymentRequests;

  private readonly validateApplePayMerchant: ValidateApplePayMerchant;

  private session?: ApplePaySessionLike;

  onComplete?: (result: AuthorizeApplePayResult) => void;

  onFailed?: (event?: ApplePayEvent) => void;

  onCancel?: () => void;

  constructor(
    authorizeRequestInput: AuthorizePaymentInput,
    applePayRequest: ApplePayPaymentRequests,
    validateApplePayMerchant: ValidateApplePayMerchant
  ) {
    this.authorizeRequestInput = authorizeRequestInput;
    this.validateApplePayMerchant = validateApplePayMerchant;
    this.applePayRequest = {
      ...(applePayRequest || {}),
      merchantCapabilities: applePayRequest?.merchantCapabilities || applePayDefaultOption.merchantCapabilities,
      supportedNetworks: applePayRequest?.supportedNetworks || applePayDefaultOption.supportedNetworks,
    } as ApplePayPaymentRequests;
  }

  get isActive(): boolean {
    return !!this.session;
  }

  start(): void {
    if (!ApplePaySession) {
      throw new Error('ApplePaySession is not available');
    }

    if (!this.applePayRequest) {
      throw new Error('ApplePayRequest is required');
    }

    if (this.session) {
      this.abort();
    }

    this.session = new ApplePaySession(3, this.applePayRequest);

    // eslint-disable-next-line @typescript-eslint/no-misused-promises
    this.session.onvalidatemerchant = (event) => this.onValidateMerchant(event);
    this.session.onpaymentauthorized = (event) => this.onPaymentAuthorized(event);
    this.session.oncancel = () => {
      this.handleCancel();
    };

    this.session.begin();
  }

  abort(): void {
    this.session?.abort();
    this.session = undefined;
  }

  private async onValidateMerchant(event: ApplePayValidateMerchantEvent): Promise<void> {
    if (!this.session) {
      throw new Error('Session is required');
    }

    // eslint-disable-next-line @typescript-eslint/no-redundant-type-constituents
    let merchantSession: unknown | null;

    try {
      if (this.validateApplePayMerchant) {
        merchantSession = await this.validateApplePayMerchant({
          validationURL: event.validationURL,
        });
      }
    } catch (error) {
      this.abort();
      this.onFailed?.(Object.assign(event, { sessionError: error }));
      return;
    }

    if (!merchantSession || !this.session) {
      this.abort();
      this.onFailed?.(event);
      return;
    }

    this.session.completeMerchantValidation(merchantSession);
  }

  private onPaymentAuthorized(event: ApplePayPaymentAuthorizedEvent): void {
    if (!this.session) {
      this.handleCancel();
      return;
    }
    const token = event.payment?.token;
    let paymentStatus: number;

    if (!token) {
      const statusFailure = ApplePaySession?.STATUS_FAILURE ?? 1;
      paymentStatus = statusFailure;
      this.onFailed?.(event);
    } else {
      const statusSuccess = ApplePaySession?.STATUS_SUCCESS ?? 0;
      paymentStatus = statusSuccess;
      this.onComplete?.(token);
    }

    this.session.completePayment(paymentStatus);
    this.session = undefined;
  }

  private handleCancel(): void {
    this.session = undefined;
    this.onCancel?.();
  }
}
